import { useEffect, useState } from "react"
import "./completion.scss"
import { TODO_LABEL, TODO_ROOT_CLASS, TodoKind, countTodos, todoAvailable } from "./completion"

const KINDS: TodoKind[] = ["data", "draft", "wrong"]

/**
 * The toggle for the completion overlay. Renders nothing at all outside dev unless `?todo`
 * is on the URL, so it cannot reach a client by accident.
 *
 * Counts are read from the DOM after paint rather than kept in a registry: the marks live
 * on the elements themselves, so the document is the only thing that can't fall out of date.
 */
export function CompletionOverlay() {
  const [on, setOn] = useState(false)
  const [counts, setCounts] = useState<Record<TodoKind, number>>({ data: 0, draft: 0, wrong: 0 })

  useEffect(() => {
    if (!todoAvailable()) return
    document.documentElement.classList.toggle(TODO_ROOT_CLASS, on)
    return () => document.documentElement.classList.remove(TODO_ROOT_CLASS)
  }, [on])

  // Re-count on the frame after a toggle: rows expand and dialogs open, so the marked set
  // changes as the screen is used.
  useEffect(() => {
    if (!todoAvailable() || !on) return
    const id = window.setInterval(() => setCounts(countTodos()), 600)
    setCounts(countTodos())
    return () => window.clearInterval(id)
  }, [on])

  if (!todoAvailable()) return null

  const total = KINDS.reduce((a, k) => a + counts[k], 0)

  return (
    <div className="todo-dock">
      {on && (
        <div className="todo-legend">
          {KINDS.map((k) => (
            <div className="todo-legend__row" key={k}>
              <span className={`todo-legend__swatch todo-legend__swatch--${k}`} />
              {TODO_LABEL[k]}
              <span className="todo-legend__n">{counts[k]}</span>
            </div>
          ))}
          <div className="todo-legend__hint">
            Hover any marked element for what it needs. Expand a network row or open a dialog
            to reveal more.
          </div>
        </div>
      )}
      <button
        type="button"
        className={`todo-toggle${on ? " todo-toggle--on" : ""}`}
        onClick={() => setOn((v) => !v)}
        title="Outline everything on this screen that still needs data or wiring"
      >
        <i className="bi bi-exclamation-diamond" />
        Incomplete
        {on && total > 0 && <span className="todo-toggle__count">{total}</span>}
      </button>
    </div>
  )
}
